import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Grid, Icon, Input, Message } from 'semantic-ui-react';
import {
  RejectedByDto,
  RejectedByStore,
  NewRejectedByStore,
  ChangeRejectedByStore,
  CreateRejectedByDto,
  ChangeRejectedByDto
} from 'stores/configuration/events-n-requests/rejected-by-store';
import { ItemState, OrderDefinition, Query } from 'stores/dataStore';
import { CommandResult } from 'stores/types';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { TextBoxFilter } from 'widgets/collections/table-filters/textbox-filter';
import RejectedByManager from './rejected-by-manager';

interface RejectedByProps extends WithTranslation {
  rejectedByStore?: RejectedByStore;
  newRejectedByStore?: NewRejectedByStore;
  changeRejectedByStore?: ChangeRejectedByStore;
}

export type ModalMode = 'edit' | 'new';
interface RejectedByState {
  query: Query;
  rejectedByManager: boolean;
  activeFilters: string[];
  selectedItem: RejectedByDto;
  mode: ModalMode;
  someFilterOpened: boolean;
}
@connect(['rejectedByStore', RejectedByStore], ['newRejectedByStore', NewRejectedByStore], ['changeRejectedByStore', ChangeRejectedByStore])
class RejectedByList extends Component<RejectedByProps, RejectedByState> {
  state: RejectedByState = {
    query: { searchQuery: '', orderBy: [{ direction: 'Descending', field: 'modifiedOn', useProfile: false }], skip: 0, take: 10 },
    rejectedByManager: false,
    activeFilters: [],
    selectedItem: null,
    mode: null,
    someFilterOpened: false
  };

  handleOnEnterKeydown = item => {
    this.onEditItem(item);
  };

  handleOnRowDoubleClick = item => {
    this.onEditItem(item);
  };

  componentDidMount() {
    this.load();
  }

  private load = (query: Query = this.state.query) => this.props.rejectedByStore.getAllAsync(query);

  private handleOrderBy = (newOrder: OrderDefinition[]) => {
    const orderBy: OrderDefinition[] = [...newOrder, { direction: 'Descending', field: 'modifiedOn', useProfile: false }];
    this.setState(({ query }) => ({ query: { ...query, orderBy } }), this.load);
  };

  private handlePageChange = (skip: number, take: number) => this.setState(({ query }) => ({ query: { ...query, skip, take } }), this.load);

  private onSaveRow = async (item: RejectedByDto, state: ItemState): Promise<CommandResult<any>> => {
    if (state !== 'New') await this.props.rejectedByStore.saveAsync(item, state);

    return { isSuccess: true, items: [], messages: [] };
  };

  private onDelete = async (item: RejectedByDto, state: ItemState): Promise<CommandResult<any>> => {
    if (state !== 'New') await this.props.rejectedByStore.deleteAsync(item.id, state);

    return { isSuccess: true, items: [], messages: [] };
  };

  private handleFilterChange = (filters: { id: string; filter: any }[]) => {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);
    this.setState(({ query }) => ({ query: { ...query, filter, skip: 0 }, activeFilters }), this.load);
  };

  private onNewItem = () => this.setState({ rejectedByManager: true, mode: 'new' });

  private onEditItem = (selectedItem: RejectedByDto) => this.setState({ selectedItem, mode: 'edit', rejectedByManager: true });

  private onResetFormStores = () => {
    this.props.newRejectedByStore.reset();
    this.props.changeRejectedByStore.reset();
  };
  private handleOnCloseManager = () => {
    this.setState({ rejectedByManager: false, selectedItem: null, mode: null });
    this.onResetFormStores();
  };

  private handleSaveManger = async () => {
    const { mode } = this.state;
    let response: CommandResult<ChangeRejectedByDto | CreateRejectedByDto>;
    if (mode === 'edit') response = await this.props.changeRejectedByStore.update();
    else response = await this.props.newRejectedByStore.submit();
    if (response?.isSuccess) {
      this.handleOnCloseManager();
      this.load();
    }
  };

  private handleChangeRejection = (name: string) => {
    const { selectedItem, mode } = this.state;
    this.setState(({ selectedItem }) => ({ selectedItem: { ...selectedItem, name } }));
    if (mode === 'edit') this.props.changeRejectedByStore.change({ name, id: selectedItem.id });
    else this.props.newRejectedByStore.change({ name });
    this.clearManagerErrMsgsHandler();
  };

  private clearManagerErrMsgsHandler = () => {
    const { newRejectedByStore, changeRejectedByStore } = this.props;
    (newRejectedByStore.state.result?.messages || []).length && newRejectedByStore.clearMessages();
    (changeRejectedByStore.state.result?.messages || []).length && changeRejectedByStore.clearMessages();
  };

  componentWillUnmount() {
    this.onResetFormStores();
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t, rejectedByStore, newRejectedByStore, changeRejectedByStore } = this.props;
    const { activeFilters, rejectedByManager, selectedItem, mode, someFilterOpened } = this.state;
    const errMsgs = [
      ...(newRejectedByStore.state.result?.messages || []).map(x => x.body),
      ...(changeRejectedByStore.state.result?.messages || []).map(x => x.body)
    ];

    const tableModel = {
      columns: [
        {
          title: t('Name'),
          tooltipRenderer: false,
          renderer: data => <span>{data?.name}</span>,
          editor: (data, onChange) => (
            <Input
              value={data.name}
              fluid
              onChange={(_, { value }) => {
                data.name = value;
                onChange();
              }}
            />
          ),
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by name')}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<RejectedByDto>('name'), `contains(${nameof<RejectedByDto>('name')}, '${value}')`)}
              onClear={() => onClear(nameof<RejectedByDto>('name'))}
              active={activeFilters.includes(nameof<RejectedByDto>('name'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: { field: nameof<RejectedByDto>('name'), useProfile: false }
        }
      ],
      data: rejectedByStore.state
    } as TableModel<RejectedByDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {rejectedByStore.state.result && !rejectedByStore.state.result.isSuccess && (rejectedByStore.state.result.messages || []).length && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                onDismiss={rejectedByStore.clearMessages}
                error
                header={t('An error ocurred')}
                list={rejectedByStore.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!rejectedByManager}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={[
                {
                  content: (
                    <>
                      <Icon name="edit" />
                      &nbsp;{t('Edit')}
                    </>
                  ),
                  onClick: this.onEditItem
                }
              ]}
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew
              createNewButtonTitle={t('Add Rejected By')}
            />
          </Grid.Row>
        </Grid>
        {rejectedByManager && (
          <RejectedByManager
            onChange={this.handleChangeRejection}
            onSubmit={this.handleSaveManger}
            onClose={this.handleOnCloseManager}
            onClearErrors={this.clearManagerErrMsgsHandler}
            editItem={selectedItem}
            mode={mode}
            errMsgs={errMsgs}
            isBusy={newRejectedByStore.state.isBusy || changeRejectedByStore.state.isBusy}
          />
        )}
      </>
    );
  }
}
export default withTranslation()(RejectedByList);
